/* ==========================================================================
   SOUNDLABZ — tools.js
   Box calculator + wiring visualizer.

   The numbers here are the rules of thumb a good installer uses before
   pulling the actual T/S parameters. They're deliberately conservative and
   every panel says so — the goal is to get someone into the right ballpark
   and then into a conversation, not to replace WinISD.
   ========================================================================== */
(function () {
  'use strict';

  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  /* Wires a group of aria-pressed buttons as a single-choice control. */
  function seg(root, sel, onChange) {
    var group = $(sel, root);
    if (!group) return function () { return null; };
    group.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-v]');
      if (!btn) return;
      $$('button[data-v]', group).forEach(function (b) {
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
      onChange();
    });
    return function () {
      var on = $('button[aria-pressed="true"]', group);
      return on ? on.getAttribute('data-v') : null;
    };
  }

  /* ==================================================== BOX CALCULATOR */

  // Net internal volume in cubic feet, per driver.
  var VOL = {
    sealed: {
      '8':  { sq: 0.35, daily: 0.45, spl: 0.55 },
      '10': { sq: 0.60, daily: 0.75, spl: 0.90 },
      '12': { sq: 0.90, daily: 1.25, spl: 1.50 },
      '15': { sq: 1.50, daily: 2.00, spl: 2.50 },
      '18': { sq: 2.50, daily: 3.20, spl: 4.00 }
    },
    ported: {
      '8':  { sq: 0.70, daily: 0.90, spl: 1.10 },
      '10': { sq: 1.10, daily: 1.40, spl: 1.75 },
      '12': { sq: 1.75, daily: 2.25, spl: 2.75 },
      '15': { sq: 3.00, daily: 3.75, spl: 4.50 },
      '18': { sq: 4.50, daily: 5.50, spl: 6.50 }
    }
  };

  // Driver displacement, cu ft each — the volume the magnet and cone steal back.
  var DISP = { '8': 0.06, '10': 0.09, '12': 0.13, '15': 0.22, '18': 0.32 };

  // Typical mounting depth/height used to derive plausible outer dimensions.
  var BOXD = { '8': [12, 10], '10': [14, 12], '12': [15, 14], '15': [18, 16], '18': [21, 19] };

  // Port area per cubic foot of net volume, sq in. More area = less chuffing.
  var PORTAREA = { sq: 12, daily: 14, spl: 16 };

  function boxCalc(root) {
    var getType = seg(root, '[data-box-type]', run);
    var getSize = seg(root, '[data-box-size]', run);
    var getQty  = seg(root, '[data-box-qty]', run);
    var getGoal = seg(root, '[data-box-goal]', run);
    var getPort = seg(root, '[data-box-port]', run);

    var tuning = $('[data-box-tuning]', root);
    var tuningWrap = $('[data-box-tuning-wrap]', root);
    var tuningVal = $('[data-box-tuning-val]', root);
    if (tuning) tuning.addEventListener('input', run);

    function out(k, v) {
      var el = $('[data-box-out="' + k + '"]', root);
      if (el) el.innerHTML = v;
    }
    function row(k, show) {
      var el = $('[data-box-row="' + k + '"]', root);
      if (el) el.hidden = !show;
    }

    function run() {
      var type = getType() || 'sealed';
      var size = getSize() || '12';
      var qty = parseInt(getQty() || '1', 10);
      var goal = getGoal() || 'daily';
      var ported = type === 'ported';
      var fb = tuning ? parseFloat(tuning.value) : 32;

      if (tuningWrap) tuningWrap.hidden = !ported;
      if (tuningVal) tuningVal.textContent = fb;
      row('port', ported);
      row('portlen', ported);

      var netEach = VOL[type][size][goal];
      var net = netEach * qty;
      var disp = DISP[size] * qty;

      // Port itself displaces volume too — roughly 12% of net on a slot build.
      var portDisp = ported ? net * 0.12 : 0;
      // Bracing and glue joints eat a little more.
      var brace = net * 0.05;
      var gross = net + disp + portDisp + brace;

      out('vol', net.toFixed(2));
      out('gross', gross.toFixed(2) + ' cu ft');
      out('disp', '+' + (disp + portDisp + brace).toFixed(2) + ' cu ft');

      if (ported) {
        var area = net * PORTAREA[goal];
        out('parea', area.toFixed(0) + ' sq in');

        // Helmholtz, with end correction. Vb in cubic inches.
        var vbCuIn = net * 1728;
        var r = Math.sqrt(area / Math.PI);
        var len = (1.463e7 * r * r) / (fb * fb * vbCuIn) - 1.463 * r;

        // A slot port sharing box walls gets more end correction than a
        // free-standing aero, so it runs a touch shorter in practice.
        if (getPort() === 'slot') len *= 0.94;

        if (len < 2) {
          out('plen', 'under 2&Prime; — tune lower or use less port');
        } else {
          out('plen', len.toFixed(1) + '&Prime;');
        }
      }

      // Plausible outer dimensions from a typical height and depth.
      var hd = BOXD[size];
      var h = hd[0], d = hd[1];
      var w = (gross * 1728) / (h * d);
      var ow = (w + 1.5).toFixed(1), oh = (h + 1.5).toFixed(1), od = (d + 1.5).toFixed(1);
      out('dims', ow + '&Prime; W &times; ' + oh + '&Prime; H &times; ' + od + '&Prime; D');

      // 3/4" MDF, surface area plus a doubled baffle and 20% waste.
      var sqin = 2 * (w * h + w * d + h * d) + (w * h);
      var sheets = (sqin / 144) * 1.2 / 32;
      out('mdf', (Math.ceil(sheets * 2) / 2).toFixed(1) + ' sheets of 3/4&Prime;');

      // Sanity flag — a box this wide won't fit through a trunk opening.
      var flag = '';
      if (w > 46) {
        flag = '<span class="calc__flag calc__flag--warn">That\'s a wide box — measure your trunk or plan a split enclosure</span>';
      } else if (ported && fb > 40 && goal !== 'spl') {
        flag = '<span class="calc__flag calc__flag--warn">Tuning this high will sound thin on music</span>';
      } else {
        flag = '<span class="calc__flag calc__flag--ok">Sensible starting point</span>';
      }
      out('flag', flag);
    }

    run();
  }

  /* ================================================ WIRING VISUALIZER */

  function wiring(root) {
    var getCoils = seg(root, '[data-wire-coils]', run);
    var getOhms  = seg(root, '[data-wire-ohms]', run);
    var getQty   = seg(root, '[data-wire-qty]', run);
    var getMode  = seg(root, '[data-wire-mode]', run);
    var svg = $('[data-wire-svg]', root);

    function out(k, v) {
      var el = $('[data-wire-out="' + k + '"]', root);
      if (el) el.innerHTML = v;
    }

    function run() {
      var coils = parseInt(getCoils() || '2', 10);
      var z = parseFloat(getOhms() || '2');
      var qty = parseInt(getQty() || '1', 10);
      var mode = getMode() || 'parallel';

      // Everything reduces to: some number of identical series chains,
      // all of those chains in parallel. One model, three modes.
      var groups, perGroup;
      if (mode === 'parallel') { groups = coils * qty; perGroup = 1; }
      else if (mode === 'series') { groups = 1; perGroup = coils * qty; }
      else { groups = qty; perGroup = coils; }

      var load = (z * perGroup) / groups;

      out('load', fmt(load) + ' &Omega;');
      out('loadbig', fmt(load) + '&Omega;');
      out('coils', (coils * qty) + ' total');

      // What the amp has to be rated for.
      var stable;
      if (load < 0.4) stable = 'nothing standard — rethink this';
      else if (load < 0.9) stable = '0.5&Omega; stable (competition amp)';
      else if (load < 1.9) stable = '1&Omega; stable';
      else if (load < 3.9) stable = '2&Omega; stable';
      else if (load < 7.9) stable = '4&Omega; stable';
      else stable = '8&Omega; — very safe, very quiet';
      out('stable', stable);

      var flag = '';
      if (load < 0.9) {
        flag = '<span class="calc__flag calc__flag--warn">Sub-1&Omega; — most amps will go into protect or die here</span>';
      } else if (load >= 8) {
        flag = '<span class="calc__flag calc__flag--warn">You\'re leaving most of the amp\'s power on the table</span>';
      } else {
        flag = '<span class="calc__flag calc__flag--ok">Safe for a standard ' + fmt(load) + '&Omega; stable amp</span>';
      }
      out('flag', flag);

      draw(groups, perGroup, z, qty, coils);
    }

    function fmt(n) {
      return (Math.round(n * 100) / 100).toString().replace(/\.00$/, '');
    }

    function el(tag, attrs, text) {
      var n = document.createElementNS('http://www.w3.org/2000/svg', tag);
      for (var k in attrs) n.setAttribute(k, attrs[k]);
      if (text != null) n.textContent = text;
      return n;
    }

    function draw(groups, perGroup, z, qty, coils) {
      if (!svg) return;
      while (svg.childNodes.length > 1) svg.removeChild(svg.lastChild);

      var W = 460, H = 300;
      var TOP = 30, BOT = 272;       // the two bus bars
      var x0 = 88, x1 = 436;
      var colW = groups > 1 ? (x1 - x0) / (groups - 1) : 0;

      // Amplifier
      svg.appendChild(el('rect', { x: 8, y: 122, width: 52, height: 58, rx: 8, fill: 'var(--ink)' }));
      svg.appendChild(el('text', {
        x: 34, y: 148, 'text-anchor': 'middle', fill: '#fff',
        'font-family': 'JetBrains Mono, monospace', 'font-size': 10
      }, 'AMP'));
      svg.appendChild(el('text', {
        x: 34, y: 165, 'text-anchor': 'middle', fill: '#00e05a',
        'font-family': 'JetBrains Mono, monospace', 'font-size': 11, 'font-weight': 700
      }, fmt((z * perGroup) / groups) + 'Ω'));

      // Amp terminals up to the + bus and down to the − bus
      svg.appendChild(el('path', {
        d: 'M60 134 H74 V' + TOP + ' H' + x1,
        stroke: '#00b84a', 'stroke-width': 2.5, fill: 'none', class: 'wiring__wire'
      }));
      svg.appendChild(el('path', {
        d: 'M60 168 H74 V' + BOT + ' H' + x1,
        stroke: 'var(--ink)', 'stroke-width': 2.5, fill: 'none', class: 'wiring__wire'
      }));

      svg.appendChild(el('text', { x: 80, y: TOP - 8, fill: '#00994a', 'font-family': 'JetBrains Mono, monospace', 'font-size': 10 }, '+'));
      svg.appendChild(el('text', { x: 80, y: BOT + 16, fill: 'var(--ink-2)', 'font-family': 'JetBrains Mono, monospace', 'font-size': 10 }, '−'));

      // Each group is a vertical series chain hanging between the buses.
      var span = BOT - TOP;
      var slot = span / perGroup;
      var boxH = Math.min(34, slot * 0.62);

      for (var g = 0; g < groups; g++) {
        var cx = groups === 1 ? (x0 + x1) / 2 : x0 + g * colW;

        for (var c = 0; c < perGroup; c++) {
          var cy = TOP + slot * c + (slot - boxH) / 2;

          // Connector into this coil
          var prevY = c === 0 ? TOP : TOP + slot * c - (slot - boxH) / 2;
          svg.appendChild(el('line', {
            x1: cx, y1: prevY, x2: cx, y2: cy,
            stroke: c === 0 ? '#00b84a' : 'var(--ink-3)', 'stroke-width': 2, class: 'wiring__wire'
          }));

          svg.appendChild(el('rect', {
            x: cx - 21, y: cy, width: 42, height: boxH, rx: 6,
            fill: '#fff', stroke: 'var(--line)', 'stroke-width': 1.5
          }));
          svg.appendChild(el('text', {
            x: cx, y: cy + boxH / 2 + 3.5, 'text-anchor': 'middle',
            fill: 'var(--ink)', 'font-family': 'JetBrains Mono, monospace', 'font-size': 10, 'font-weight': 700
          }, z + 'Ω'));
        }

        // Tail down to the − bus
        var lastBottom = TOP + slot * (perGroup - 1) + (slot - boxH) / 2 + boxH;
        svg.appendChild(el('line', {
          x1: cx, y1: lastBottom, x2: cx, y2: BOT,
          stroke: 'var(--ink)', 'stroke-width': 2, class: 'wiring__wire'
        }));
      }

      // Caption
      var label = groups === 1
        ? 'All ' + perGroup + ' coils in series'
        : perGroup === 1
          ? 'All ' + groups + ' coils in parallel'
          : groups + ' chains of ' + perGroup + ', wired in parallel';
      svg.appendChild(el('text', {
        x: W / 2, y: H - 4, 'text-anchor': 'middle',
        fill: 'var(--ink-2)', 'font-family': 'JetBrains Mono, monospace', 'font-size': 9.5
      }, label));
    }

    run();
  }

  /* ============================================================ TABS */
  function tabs() {
    $$('[data-tools-tabs]').forEach(function (root) {
      if (root.__t) return; root.__t = 1;
      var btns = $$('.tools__tab', root);
      btns.forEach(function (b) {
        b.addEventListener('click', function () {
          btns.forEach(function (o) {
            var on = o === b;
            o.setAttribute('aria-selected', on ? 'true' : 'false');
            var panel = document.getElementById(o.getAttribute('aria-controls'));
            if (panel) panel.hidden = !on;
          });
        });
      });
    });
  }

  function init() {
    $$('[data-box]').forEach(function (r) { if (!r.__b) { r.__b = 1; boxCalc(r); } });
    $$('[data-wiring]').forEach(function (r) { if (!r.__w) { r.__w = 1; wiring(r); } });
    tabs();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();

/* ==========================================================================
   fitment.js — vehicle picker
   Reads a pipe-delimited list out of the section schema so the shop owner
   can maintain it in the theme editor without touching code.
   ========================================================================== */
(function () {
  'use strict';
  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  function parse(raw) {
    return raw.split('\n').map(function (line) {
      var p = line.split('|').map(function (s) { return s.trim(); });
      if (p.length < 4) return null;
      var yrs = (p[2] || '').split('-');
      return {
        make: p[0], model: p[1],
        from: parseInt(yrs[0], 10) || 1990,
        to: parseInt(yrs[1], 10) || new Date().getFullYear(),
        front: p[3] || '', rear: p[4] || '', box: p[5] || '', note: p[6] || ''
      };
    }).filter(Boolean);
  }

  function fill(sel, items, placeholder) {
    sel.innerHTML = '<option value="">' + placeholder + '</option>' +
      items.map(function (i) { return '<option value="' + i + '">' + i + '</option>'; }).join('');
    sel.disabled = !items.length;
  }

  function uniq(a) { return a.filter(function (v, i) { return a.indexOf(v) === i; }).sort(); }

  function init() {
    $$('[data-fitment]').forEach(function (root) {
      if (root.__f) return; root.__f = 1;

      var script = $('[data-fit-data]', root.closest('section'));
      if (!script) return;
      var rows;
      try { rows = parse(JSON.parse(script.textContent)); }
      catch (e) { return; }

      var yearEl = $('[data-fit="year"]', root);
      var makeEl = $('[data-fit="make"]', root);
      var modelEl = $('[data-fit="model"]', root);
      var out = $('[data-fit-result]', root);

      var now = new Date().getFullYear();
      var years = [];
      for (var y = now + 1; y >= 1995; y--) years.push(String(y));
      fill(yearEl, years, 'Select year');

      yearEl.addEventListener('change', function () {
        var y = parseInt(yearEl.value, 10);
        var makes = uniq(rows.filter(function (r) { return y >= r.from && y <= r.to; })
                             .map(function (r) { return r.make; }));
        fill(makeEl, makes, makes.length ? 'Select make' : 'Nothing listed — text us');
        fill(modelEl, [], 'Select model');
        out.innerHTML = '';
      });

      makeEl.addEventListener('change', function () {
        var y = parseInt(yearEl.value, 10);
        var models = uniq(rows.filter(function (r) {
          return r.make === makeEl.value && y >= r.from && y <= r.to;
        }).map(function (r) { return r.model; }));
        fill(modelEl, models, 'Select model');
        out.innerHTML = '';
      });

      $('[data-fit-go]', root).addEventListener('click', function () {
        var y = parseInt(yearEl.value, 10);
        var hit = rows.filter(function (r) {
          return r.make === makeEl.value && r.model === modelEl.value && y >= r.from && y <= r.to;
        })[0];

        if (!hit) {
          out.innerHTML = '<p>We don\'t have that one on file yet — but we almost certainly have parts for it. ' +
            'Text us the year, make and model and we\'ll come back with exact sizes.</p>';
          return;
        }

        var chips = [];
        if (hit.front) chips.push('Front ' + hit.front);
        if (hit.rear) chips.push('Rear ' + hit.rear);
        if (hit.box) chips.push('Max sub ' + hit.box);

        out.innerHTML =
          '<p><b>' + y + ' ' + hit.make + ' ' + hit.model + '</b></p>' +
          '<div class="fitment__spec">' +
            chips.map(function (c) { return '<span class="chip">' + c + '</span>'; }).join('') +
          '</div>' +
          (hit.note ? '<p style="margin-top:.8rem;color:var(--ink-2)">' + hit.note + '</p>' : '') +
          '<p style="margin-top:1rem;font-size:var(--step--1);color:var(--ink-2)">' +
          'Sizes are the factory openings. Adapters open up more options — ask us.</p>';
      });
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
